/**
 * team_credits block helpers.
 *
 * Stored block_content shape:
 * [
 *   {
 *     member: string,          // display name
 *     member_id: string,       // student / application id when known
 *     student_email: string,
 *     avatar: string,          // square image URL (Qiniu)
 *     avatar_url: string,      // alias for portfolio
 *     role: string,            // member role shown on Portfolio
 *     contribution: string
 *   }
 * ]
 */

const LEGACY_TECH_DOMAIN_REPLACEMENTS = [
    // CDN / media hosts
    [/(\bhttps?:\/\/)?ap\.learningfirst\.tech\b/gi, 'https://ap.learningfirst.io'],
    [/(\bhttps?:\/\/)?ap\.learningfrist\.tech\b/gi, 'https://ap.learningfirst.io'],
    // Any other learningfirst.tech / typo host → .io (keep subdomain)
    [/\b([a-z0-9-]+)\.learningfirst\.tech\b/gi, '$1.learningfirst.io'],
    [/\b([a-z0-9-]+)\.learningfrist\.tech\b/gi, '$1.learningfirst.io'],
    [/(\bhttps?:\/\/)?view\.certifi\.tech\b/gi, 'https://view.certifi.io']
]

export function migrateLegacyTechUrl(url) {
    if (!url) {
        return ''
    }
    let normalized = String(url).trim()
    LEGACY_TECH_DOMAIN_REPLACEMENTS.forEach(([pattern, replacement]) => {
        normalized = normalized.replace(pattern, replacement)
    })
    // Collapse accidental double protocol from host-only replacements
    normalized = normalized.replace(/^https:\/\/https:\/\//i, 'https://')
    normalized = normalized.replace(/^http:\/\/https:\/\//i, 'https://')
    return normalized
}

/**
 * Normalize a stored media/attachment URL for display:
 * migrate legacy .tech CDN hosts and ensure an absolute http(s) URL.
 */
export function resolveDisplayMediaUrl(url) {
    if (!url) {
        return ''
    }
    let value = migrateLegacyTechUrl(String(url).trim())
    if (!value) {
        return ''
    }
    if (/^\/\//.test(value)) {
        value = 'https:' + value
    } else if (!/^https?:\/\//i.test(value) && !/^blob:/i.test(value) && !/^data:/i.test(value)) {
        value = 'https://' + value.replace(/^\/+/, '')
    }
    return migrateLegacyTechUrl(value)
}

export function createEmptyTeamCreditMember() {
    return {
        member: '',
        member_id: '',
        student_email: '',
        role: '',
        avatar: '',
        avatar_url: '',
        contribution: ''
    }
}

export function normalizeTeamCreditsContent(stored) {
    const list = Array.isArray(stored)
        ? stored
        : (stored ? [stored] : [])

    const normalized = list.map(item => {
        if (!item || typeof item !== 'object') {
            // Legacy plain-string rows → put into contribution
            const text = item == null ? '' : String(item)
            return {
                ...createEmptyTeamCreditMember(),
                contribution: text
            }
        }
        const avatar = item.avatar || item.avatar_url || item.image || ''
        return {
            member: item.member || item.name || item.student_name || '',
            member_id: item.member_id || item.student_id || item.id || '',
            student_email: item.student_email || item.email || '',
            role: item.role || item.member_role || '',
            avatar: resolveDisplayMediaUrl(avatar),
            avatar_url: resolveDisplayMediaUrl(item.avatar_url || avatar),
            contribution: item.contribution || item.credits || item.text || ''
        }
    }).filter(row => {
        // Keep empty starter row; drop only completely useless legacy empties later if needed
        return true
    })

    return normalized.length ? normalized : [createEmptyTeamCreditMember()]
}

export function formatTeamMemberDisplayName(student) {
    if (!student) {
        return ''
    }
    if (typeof student === 'string') {
        return student
    }
    const given = student.student_givenName_pinyin ||
        student.student_givenName ||
        student.givenName_pinyin ||
        student.givenName ||
        ''
    const last = student.student_lastName_pinyin ||
        student.student_lastName ||
        student.lastName_pinyin ||
        student.lastName ||
        ''
    const full = [given, last].filter(Boolean).join(' ').trim()
    return full ||
        student.student_name ||
        student.name ||
        student.student_email ||
        student.email ||
        ''
}

export function formatTeamMemberRole(role) {
    const value = String(role || '').trim()
    if (!value) {
        return ''
    }
    if (value === '领队') {
        return 'Leader'
    }
    if (value === '队员') {
        return 'Member'
    }
    return value
}

export function buildQiniuPublicUrl(key) {
    if (!key) {
        return ''
    }
    const raw = String(key).trim()
    if (/^https?:\/\//i.test(raw) || /^\/\//.test(raw)) {
        return resolveDisplayMediaUrl(raw)
    }
    const normalizedKey = raw.replace(/^\/+/, '')
    const encodedKey = normalizedKey
        .split('/')
        .map(part => encodeURIComponent(part))
        .join('/')
    return 'https://ap.learningfirst.io/' + encodedKey
}

export function mapStudentsToMemberOptions(students) {
    if (!students || !students.length) {
        return []
    }
    const seen = {}
    const options = []
    students.forEach(student => {
        const name = formatTeamMemberDisplayName(student)
        const email = (student && (student.student_email || student.email)) || ''
        const id = (student && (
            (student._id && student._id.$id) ||
            student.student_id ||
            student.id ||
            email ||
            name
        )) || name
        const key = String(email || id || name).toLowerCase()
        if (!key || seen[key]) {
            return
        }
        seen[key] = true
        options.push({
            id: String(id),
            name,
            student_email: email,
            role: formatTeamMemberRole(
                (student && (student.role || student.application_team_role || student.team_role)) || ''
            ),
            raw: student
        })
    })
    return options.filter(opt => opt.name || opt.id)
}

/**
 * Prefer structured member rows when resolving credits for one student.
 */
export function findMemberContribution(members, studentInfo) {
    const rows = normalizeTeamCreditsContent(members)
    const info = studentInfo || {}
    const given = info.student_givenName_pinyin || info.student_givenName || ''
    const last = info.student_lastName_pinyin || info.student_lastName || ''
    const full = [given, last].filter(Boolean).join(' ').trim().toLowerCase()
    const email = String(info.student_email || info.email || '').toLowerCase()
    const sid = String(info.student_id || info._id || (info._id && info._id.$id) || '')

    const hit = rows.find(row => {
        if (sid && row.member_id && String(row.member_id) === sid) {
            return true
        }
        if (email && row.student_email && String(row.student_email).toLowerCase() === email) {
            return true
        }
        if (full && row.member && String(row.member).toLowerCase() === full) {
            return true
        }
        return false
    })

    if (!hit || !hit.contribution) {
        return { text: '', role: '', source: 'unmatched', avatar: '' }
    }
    return {
        text: String(hit.contribution).trim(),
        role: formatTeamMemberRole(hit.role || hit.member_role || ''),
        source: 'team_credits',
        avatar: hit.avatar || hit.avatar_url || ''
    }
}
